import { useEffect, useRef, useState } from "react";
import { MapPin, Navigation, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { Input } from "@/components/ui/input";

export function PlaceSearch({ onPick }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const close = (e) => { if (!boxRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const search = async (e) => {
    e.preventDefault();
    if (q.trim().length < 2) return;
    setBusy(true);
    try {
      const { data } = await api.get("/map/layers/geocode", { params: { q } });
      setResults(data);
      setOpen(true);
      if (data.length === 0) toast.info("No places found in India for that search");
      else if (data.length === 1) pick(data[0]);
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  const pick = (r) => {
    setOpen(false);
    setQ(r.label);
    onPick(r.bounds ? { bounds: r.bounds, maxZoom: 15 } : { center: [r.lat, r.lng], zoom: 14 });
  };

  return (
    <div ref={boxRef} className="relative">
      <form onSubmit={search} className="glass rounded-full flex items-center gap-2 pl-3 pr-1 h-10 shadow-lg">
        <Navigation size={14} className="text-muted-foreground" />
        <Input data-testid="place-search-input" value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => results.length && setOpen(true)} placeholder="Fly to village / town" className="border-0 bg-transparent h-8 w-40 sm:w-52 focus-visible:ring-0 text-sm px-0" />
        <button type="submit" data-testid="place-search-button" disabled={busy} className="h-8 w-8 rounded-full grid place-items-center bg-emerald-900 text-white dark:bg-emerald-500 dark:text-slate-950 disabled:opacity-50" aria-label="Search place">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <MapPin size={14} />}
        </button>
      </form>
      {open && results.length > 1 && (
        <ul data-testid="place-search-results" className="absolute left-0 mt-2 w-80 glass rounded-2xl shadow-2xl p-2 z-[1300] fade-up">
          {results.map((r, i) => (
            <li key={i}>
              <button type="button" data-testid="place-search-result" onClick={() => pick(r)} className="w-full text-left text-sm px-3 py-2 rounded-xl hover:bg-emerald-50 dark:hover:bg-slate-800 flex gap-2">
                <MapPin size={14} className="mt-0.5 shrink-0 text-emerald-700" /><span className="truncate">{r.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
